import React from "react";
import { Formik } from "formik";
import { useParams } from "react-router";
import {
  getAccessLogsConfiguration,
  getCustomerDatacenters,
  getPowerReportConfigurations,
  updateAccessLogsConfiguration,
  updatePowerReportConfigurations,
} from "../../../services/customers/CustomerService";
import { Loading } from "../../../services/Loading";
import { IAccessLogsConfiguration, IDataCenter, IPowerReportConfigurations } from "../../../vm";
import { ToastContext } from "../../common/ToastProvider";
import * as yup from "yup";
import {
  Dropdown,
  IBasePickerSuggestionsProps,
  IDropdownOption,
  ITag,
  Stack,
  TagPicker,
  Toggle,
  TooltipHost,
} from "office-ui-fabric-react";
import NoDataTile from "../../common/NoDataTile";
import { parseJwt } from "../../../services/UtilService";
import { mdiCheck, mdiClose, mdiPencil } from "@mdi/js";
import Icon from "@mdi/react";
import { THEME } from "../../../utils/Styles";
import { ACCESS_LOG_DATE_FORMAT } from "../../common/Constants";

export interface LogsConfigurationProps { }

const EVENT_TYPES: ITag[] = [
  { key: "Door Open Too Long", name: "Door Open Too Long" },
  { key: "Forced Door", name: "Forced Door" },
  { key: "Passback Attempted", name: "Passback Attempted" },
  { key: "No Zone Privilege", name: "No Zone Privilege" },
];

const EVENT_TYPES_DICT: { [key: string]: string } = {
  "Door Open Too Long": "Door Open Too Long",
  "Forced Door": "Forced Door",
  "Passback Attempted": "Passback Attempted",
  "No Zone Privilege": "No Zone Privilege"
};

const LogsConfiguration: React.FC<LogsConfigurationProps> = () => {
  //custom hooks
  const params: any = useParams();
  const { showToast } = React.useContext(ToastContext);

  const tokenData = parseJwt();

  //state
  const [state, setState] = React.useState({
    isLoading: false,
    logsConfig: [],
    isOnEdit: false,
    index: undefined,
    logsConfigObj: {
      customerId: params.customerId,
      eventTypes: [],
      dateFormat: "MM/DD/YYYY hh:mm:ss A"
    },
    selectedItems: []
  } as {
    isLoading: boolean;
    logsConfig: IAccessLogsConfiguration[];
    isOnEdit: boolean;
    index?: number;
    logsConfigObj: IAccessLogsConfiguration;
    selectedItems: ITag[];
  });

  React.useEffect(() => {
    const asyncFunc = async () => {
      await getAccessLogsConfig();
    };
    asyncFunc();
  }, []);

  const getAccessLogsConfig = async () => {
    setState({ ...state, isLoading: true });
    let result = await getAccessLogsConfiguration({
      customerId: params.customerId,
    });
    if (result?.success) {
      let logsConfig: IAccessLogsConfiguration[] = [];

      if (result.data.length > 0) {
        logsConfig = result.data
      } else {
        logsConfig.push({
          customerId: params.customerId,
          eventTypes: [],
          dateFormat: "MM/DD/YYYY hh:mm:ss A"
        });
      }

      setState({
        ...state,
        logsConfig,
        isLoading: false,
      });
    } else {
      setState({ ...state, isLoading: false });
      showToast(
        result?.message || "Error while getting logs configuration",
        "error"
      );
    }
  };

  const onUpdateLogsConfig = async () => {
    setState({ ...state, isLoading: true });
    const logsConfigObj = { ...state.logsConfigObj };
    let result = await updateAccessLogsConfiguration(logsConfigObj);
    if (result?.success) {
      let logsConfig = [...state.logsConfig];
      if (state.index !== undefined) {
        logsConfig[state.index].eventTypes = result.data?.eventTypes || [];
        logsConfig[state.index].dateFormat = result.data?.dateFormat || "MM/DD/YYYY hh:mm:ss A";
      }
      setState({
        ...state,
        logsConfig,
        isOnEdit: false,
        index: undefined,
        logsConfigObj: {
          ...state.logsConfigObj,
          eventTypes: [],
          dateFormat: "MM/DD/YYYY hh:mm:ss A"
        },
        isLoading: false,
      });
      showToast("Logs configurations updated successfully", "success");
    } else {
      setState({ ...state, isLoading: false });
      showToast(
        result.message || "Error while updating logs configurations",
        "error"
      );
    }
  };

  const onEdit = (
    index: number,
    eventTypes: string[],
    dateFormat: string
  ) => {
    let selectedItems: any = eventTypes.map((d) => {
      return EVENT_TYPES.find((e) => e.key === d);
    })
    setState({
      ...state,
      isOnEdit: true,
      index,
      logsConfigObj: {
        ...state.logsConfigObj,
        eventTypes,
        dateFormat
      },
      selectedItems
    });
  };

  const listContainsTagList = (tag: ITag, tagList?: ITag[]) => {
    if (!tagList || !tagList.length || tagList.length === 0) {
      return false;
    }
    return tagList.some((compareTag) => compareTag.key === tag.key);
  };

  const filterSuggestedTags = (
    filterText: string,
    tagList: ITag[]
  ): ITag[] => {
    return filterText
      ? EVENT_TYPES.filter(
        (tag) =>
          tag.name.toLowerCase().indexOf(filterText.toLowerCase()) === 0 &&
          !listContainsTagList(tag, tagList)
      )
      : [];
  };

  const pickerSuggestionsProps: IBasePickerSuggestionsProps = {
    noResultsFoundText: "No event type found",
  };

  const showAllTags = (selectedItems?: ITag[] | undefined) => {
    return removeDuplicates(EVENT_TYPES, selectedItems);
  };

  function removeDuplicates(tagList: ITag[], tags?: ITag[]) {
    return tagList.filter((tag) => !listContainsTagList(tag, tags));
  }

  return (
    <React.Fragment>
      {state.isLoading && <Loading />}
      <div>
        {state.logsConfig && state.logsConfig.length > 0 ? (
          <div className="minimum-committed-table">
            <Formik
              enableReinitialize
              initialValues={state.logsConfigObj}
              onSubmit={async (
                values: IAccessLogsConfiguration,
                { setSubmitting }
              ) => {
                setSubmitting(false);
              }}
            >
              {({ handleSubmit, values }) => (
                <form onSubmit={handleSubmit}>
                  <Stack className="pt-16">
                    <Stack.Item>
                      <div className="office-table bg-white">
                        <table className="ms-Table bordered-table w-100">
                          <thead className="html-table-thead">
                            <tr style={{ height: 44 }}>
                              <th>Event Types</th>
                              <th>Date Format</th>
                              {![2, 4, 5].includes(tokenData.role) && <th>Actions</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {state.logsConfig.map(
                              (
                                ele: IAccessLogsConfiguration,
                                index: number
                              ) => {
                                return (
                                  <tr key={index}>
                                    {state.isOnEdit && state.index === index
                                      ?
                                      <td>
                                        <TagPicker
                                          selectedItems={state.selectedItems}
                                          removeButtonAriaLabel="Remove"
                                          onResolveSuggestions={
                                            filterSuggestedTags as any
                                          }
                                          pickerSuggestionsProps={
                                            pickerSuggestionsProps
                                          }
                                          inputProps={{
                                            placeholder: "Select event types",
                                          }}
                                          onChange={(items: any) => {
                                            let eventTypes: any = [];
                                            items.forEach(
                                              (element: any) => {
                                                eventTypes.push(element.key);
                                              }
                                            );
                                            setState({
                                              ...state,
                                              logsConfigObj: {
                                                ...state.logsConfigObj,
                                                eventTypes,
                                              },
                                              selectedItems: items
                                            });
                                          }}
                                          onEmptyResolveSuggestions={
                                            showAllTags
                                          }
                                        />
                                      </td>
                                      :
                                      <td
                                        align={
                                          ele.eventTypes.length < 1
                                            ? "center"
                                            : "left"
                                        }
                                      >
                                        {ele.eventTypes.length > 0
                                          ? ele.eventTypes
                                            .map((d) => EVENT_TYPES_DICT[d])
                                            .join(", ")
                                          : "-"}
                                      </td>
                                    }
                                    {state.isOnEdit && state.index === index
                                      ?
                                      <td width={250}>
                                        <Dropdown
                                          placeholder="Select a date format"
                                          selectedKey={
                                            state.logsConfigObj
                                              .dateFormat
                                          }
                                          // styles={{ dropdown: { width: 200 } }}
                                          options={Object.keys(ACCESS_LOG_DATE_FORMAT).map((d) => {
                                            return { key: d, text: ACCESS_LOG_DATE_FORMAT[d] }
                                          })}
                                          onChange={(
                                            e,
                                            v: IDropdownOption | undefined
                                          ) => {
                                            if (v) {
                                              setState({
                                                ...state,
                                                logsConfigObj: {
                                                  ...state.logsConfigObj,
                                                  dateFormat:
                                                    v.key as string,
                                                },
                                              });
                                            }
                                          }}
                                        />
                                      </td>
                                      :
                                      <td
                                        align="center"
                                      >
                                        {ACCESS_LOG_DATE_FORMAT[ele.dateFormat] || "-"}
                                      </td>
                                    }
                                    {![2, 4, 5].includes(tokenData.role) && (
                                      <td align="center">
                                        {state.isOnEdit &&
                                          state.index === index ? (
                                          <React.Fragment>
                                            <TooltipHost
                                              content="Save"
                                              calloutProps={{
                                                gapSpace: 0,
                                              }}
                                            >
                                              <span
                                                onClick={() =>
                                                  onUpdateLogsConfig()
                                                }
                                              >
                                                <Icon
                                                  path={mdiCheck}
                                                  size={0.8}
                                                  className="pointer"
                                                  color="green"
                                                />
                                              </span>
                                            </TooltipHost>
                                            &nbsp;&nbsp;
                                            <TooltipHost
                                              content="Cancel"
                                              calloutProps={{
                                                gapSpace: 0,
                                              }}
                                            >
                                              <span
                                                onClick={() =>
                                                  setState({
                                                    ...state,
                                                    isOnEdit: false,
                                                    index: undefined,
                                                    logsConfigObj: {
                                                      ...state.logsConfigObj,
                                                      eventTypes: []
                                                    },
                                                  })
                                                }
                                              >
                                                <Icon
                                                  path={mdiClose}
                                                  size={0.8}
                                                  className="pointer"
                                                  color="red"
                                                />
                                              </span>
                                            </TooltipHost>
                                          </React.Fragment>
                                        ) : (
                                          <React.Fragment>
                                            <TooltipHost
                                              content="Edit"
                                              calloutProps={{
                                                gapSpace: 0,
                                              }}
                                            >
                                              <span
                                                onClick={() =>
                                                  onEdit(
                                                    index,
                                                    ele?.eventTypes,
                                                    ele?.dateFormat
                                                  )
                                                }
                                              >
                                                <Icon
                                                  path={mdiPencil}
                                                  size={0.7}
                                                  className="pointer"
                                                  color={
                                                    THEME.palette.themePrimary
                                                  }
                                                />
                                              </span>
                                            </TooltipHost>
                                          </React.Fragment>
                                        )}
                                      </td>
                                    )}
                                  </tr>
                                );
                              }
                            )}
                          </tbody>
                        </table>
                      </div>
                    </Stack.Item>
                  </Stack>
                </form>
              )}
            </Formik>
          </div>
        ) : (
          <div className="pt-16">
            <NoDataTile title="No Logs Configuration Found for this Customer"></NoDataTile>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default LogsConfiguration;
